import { type FormEvent, useState } from 'react';
import { type InfiniteData, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { listBookings } from '../api/endpoints/bookings';
import { createCharge, listCharges, voidCharge } from '../api/endpoints/charges';
import { usePermissions } from '../auth/usePermissions';
import type { Booking, ChargeListResponse, ChargePaymentMethod, ChargeRecord } from '../api/schemas';
import { EmptyState } from '../components/EmptyState';
import { ErrorBanner } from '../components/ErrorBanner';
import { FormField } from '../components/FormField';
import { LoadingState } from '../components/LoadingState';
import { PageHeader } from '../components/PageHeader';
import { RefundReview } from '../components/RefundReview';
import { invoiceOutcome } from '../lib/autoInvoice';
import { agendaWindow, bookingsQueryKey } from '../lib/bookingWindows';
import { BOOKING_PAYMENT_LINE_NAME } from '../lib/cart';
import { toDateInputValue } from '../lib/expenses';
import { formatDateTime, formatMoneyCents } from '../lib/format';
import { parsePriceInput } from '../lib/money';
import {
  PAYMENT_METHODS,
  PAYMENT_REFERENCE_MAX,
  paymentMethodLabel,
  paymentReferenceError,
  requiresPaymentReference,
} from '../lib/paymentMethods';
import { serviceName, useBusiness } from '../lib/useBusiness';
import { useMutationAttempt } from '../lib/use-mutation-attempt';

/**
 * Pagos: the one ledger of money that came in. A paseador on the free tier
 * writes down the $15 in cash from this morning's walk; a salon on Pro sees
 * the same list fed by Cobrar and, once Dale processes a card, by Dale. One
 * row per payment, newest first. Owners can void a mistaken manual record
 * with its reason retained in the ledger.
 */

const CHARGES_QUERY_KEY = ['business-charges'];
const MAX_VOID_REASON = 300;
const DEFAULT_METHOD: ChargePaymentMethod = 'efectivo';
const SOURCE_LABEL: Record<NonNullable<ChargeRecord['source']>, string> = { manual: 'Manual', dale: 'Dale' };

export function PagosPage() {
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const seesPayments = can('pagos');
  const voids = can('anular');
  const business = useBusiness();

  const chargesQuery = useInfiniteQuery({
    queryKey: CHARGES_QUERY_KEY,
    enabled: seesPayments,
    queryFn: ({ pageParam }: { pageParam: string | undefined }) => listCharges(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });
  const charges = chargesQuery.data?.pages.flatMap((page) => page.data) ?? [];

  const bookingsRange = agendaWindow(new Date());
  const bookingsQuery = useQuery({
    queryKey: bookingsQueryKey(bookingsRange),
    queryFn: () => listBookings(bookingsRange),
  });
  const linkable = linkableBookings(bookingsQuery.data?.data ?? []);

  const [amountInput, setAmountInput] = useState('');
  const [amountError, setAmountError] = useState<string | undefined>();
  const [method, setMethod] = useState<ChargePaymentMethod>(DEFAULT_METHOD);
  const [reference, setReference] = useState('');
  const [referenceError, setReferenceError] = useState<string | undefined>();
  const [paidOn, setPaidOn] = useState(() => toDateInputValue(new Date()));
  const [bookingId, setBookingId] = useState('');
  const [note, setNote] = useState('');
  const [invoiceNote, setInvoiceNote] = useState<string | undefined>();
  const createMutation = useMutationAttempt({
    mutationFn: createCharge,
    exito: 'Pago registrado',
    onSuccess: (created) => {
      setInvoiceNote(invoiceOutcome(created, can('facturas')));
      setAmountInput('');
      setAmountError(undefined);
      setMethod(DEFAULT_METHOD);
      setReference('');
      setPaidOn(toDateInputValue(new Date()));
      setBookingId('');
      setNote('');
      void queryClient.invalidateQueries({ queryKey: CHARGES_QUERY_KEY });
    },
  });
  const saving = createMutation.isPending || createMutation.uncertain;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (createMutation.uncertain) { createMutation.retry(); return; }
    const price = parsePriceInput(amountInput);
    if (!price.ok) {
      setAmountError(price.message);
      return;
    }
    setAmountError(undefined);
    const problem = paymentReferenceError(method, reference);
    setReferenceError(problem);
    if (problem) return;
    const paymentReference = requiresPaymentReference(method) ? reference.trim() : '';
    createMutation.mutate({ items: [{ name: BOOKING_PAYMENT_LINE_NAME, unitPriceCents: price.cents, quantity: 1 }],
      paymentMethod: method, ...(paymentReference ? { paymentReference } : {}),
      expectedTotalCents: price.cents, chargedAt: chargedAtFor(paidOn),
      ...(bookingId ? { bookingId } : {}), ...(note.trim() ? { note: note.trim() } : {}) });
  }

  const today = toDateInputValue(new Date());

  return (
    <main>
      <PageHeader
        title="Pagos"
        description="Lo que entró: en efectivo, por Yappy, transferencia, Banco Aliado, tarjeta o cobrado por Dale."
      />

      <section className="section" aria-label="Registrar pago">
        <h2 className="section-label">Registrar pago</h2>
        <form className="card form-grid cols-2" onSubmit={handleSubmit}>
          <FormField label="Monto" htmlFor="pago-monto" error={amountError} hint="En dólares, por ejemplo 15.00">
            <input
              id="pago-monto"
              disabled={saving}
              inputMode="decimal"
              placeholder="15.00"
              value={amountInput}
              onChange={(event) => setAmountInput(event.target.value)}
              required
            />
          </FormField>
          <FormField label="Método" htmlFor="pago-metodo">
            <select
              id="pago-metodo"
              disabled={saving}
              value={method}
              onChange={(event) => {
                setMethod(event.target.value as ChargePaymentMethod);
                setReferenceError(undefined);
              }}
            >
              {PAYMENT_METHODS.map((entry) => (
                <option key={entry.value} value={entry.value}>
                  {entry.label}
                </option>
              ))}
            </select>
          </FormField>
          {requiresPaymentReference(method) ? (
            <FormField label="Referencia" htmlFor="pago-referencia" error={referenceError} hint="El número del comprobante">
              <input
                id="pago-referencia"
                disabled={saving}
                value={reference}
                maxLength={PAYMENT_REFERENCE_MAX}
                autoComplete="off"
                required
                onChange={(event) => setReference(event.target.value)}
              />
            </FormField>
          ) : null}
          <FormField label="Fecha" htmlFor="pago-fecha" hint="Puede ser de días atrás">
            <input
              id="pago-fecha"
              disabled={saving}
              type="date"
              value={paidOn}
              max={today}
              onChange={(event) => setPaidOn(event.target.value)}
              required
            />
          </FormField>
          <FormField label="Reserva" htmlFor="pago-reserva" hint="Opcional">
            <select id="pago-reserva" disabled={saving} value={bookingId} onChange={(event) => setBookingId(event.target.value)}>
              <option value="">Sin reserva</option>
              {linkable.map((booking) => (
                <option key={booking.id} value={booking.id}>
                  {formatDateTime(booking.startsAt)} · {serviceName(business, booking.serviceId)}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Nota" htmlFor="pago-nota" className="span-2" hint="Opcional">
            <input id="pago-nota" disabled={saving} value={note} onChange={(event) => setNote(event.target.value)} maxLength={140} />
          </FormField>
          {createMutation.isError ? <ErrorBanner message={errorMessage(createMutation.error)} /> : null}
          <button type="submit" className="btn" disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Guardando…' : 'Registrar pago'}
          </button>
          {invoiceNote && !createMutation.isPending ? (
            <p className="row-sub span-2" role="status">
              {invoiceNote}
            </p>
          ) : null}
        </form>
      </section>

      {seesPayments ? <section className="section" aria-label="Pagos registrados">
        <h2 className="section-label">Pagos registrados</h2>
        {chargesQuery.isLoading ? <LoadingState label="Cargando pagos…" /> : null}
        {chargesQuery.isError ? <ErrorBanner message={errorMessage(chargesQuery.error)} /> : null}
        {!chargesQuery.isLoading && !chargesQuery.isError && charges.length === 0 ? (
          <EmptyState
            title="Todavía no hay pagos."
            description="Anota el primero arriba. Los que cobre Dale aparecen aquí solos."
          />
        ) : null}
        {charges.length > 0 ? (
          <div className="table-scroll">
            <table className="stack-table">
              <thead>
                <tr>
                  <th scope="col">Fecha</th>
                  <th scope="col">Monto</th>
                  <th scope="col">Método</th>
                  <th scope="col">Origen</th>
                  <th scope="col">Nota</th>
                  <th scope="col">Corrección</th>
                </tr>
              </thead>
              <tbody>
                {charges.map((charge) => (
                  <tr key={charge.id} className={charge.status === 'voided' ? 'muted' : undefined}>
                    <td data-cell="lead">{formatDateTime(charge.chargedAt)}</td>
                    <td data-cell="end" className="amount">{formatMoneyCents(charge.amountPaidCents ?? charge.totalCents)}</td>
                    <td data-cell="lead">
                      {paymentMethodLabel(charge.paymentMethod)}
                      {charge.paymentReference ? <p className="row-sub">Ref. {charge.paymentReference}</p> : null}
                    </td>
                    <td data-cell="end" className="muted-cell">{SOURCE_LABEL[charge.source ?? 'manual']}</td>
                    <td data-cell="full">{charge.status === 'voided' ? <><span>Anulado</span>{charge.voidReason ? <p>{charge.voidReason}</p> : null}</> : (charge.note ?? '')}</td>
                    <td data-cell="full">{voids && charge.status === 'completed' && charge.source !== 'dale' ? <VoidPayment charge={charge} /> : null}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {chargesQuery.hasNextPage ? (
          <button
            type="button"
            className="btn small secondary"
            onClick={() => void chargesQuery.fetchNextPage()}
            disabled={chargesQuery.isFetchingNextPage}
          >
            {chargesQuery.isFetchingNextPage ? 'Cargando…' : 'Ver más'}
          </button>
        ) : null}
      </section> : null}
      <RefundReview />
    </main>
  );
}

function VoidPayment({ charge }: { charge: ChargeRecord }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState('');
  const mutation = useMutationAttempt({
    mutationFn: (input: string, key) => voidCharge(charge.id, input, key),
    onSuccess: (updated) => {
      queryClient.setQueryData<InfiniteData<ChargeListResponse>>(CHARGES_QUERY_KEY, (old) => old ? {
        ...old, pages: old.pages.map((page) => ({ ...page, data: page.data.map((entry) => entry.id === updated.id ? updated : entry) })),
      } : old);
    },
  });
  const locked = mutation.isPending || mutation.uncertain;
  if (!editing) return <button type="button" className="btn small secondary" onClick={() => setEditing(true)}>Anular registro</button>;
  return <div className="form-grid">
    <p className="muted">Corrige este registro. No devuelve dinero ni anula su factura.</p>
    <FormField label="Motivo" htmlFor={`void-${charge.id}`}>
      <input id={`void-${charge.id}`} value={reason} maxLength={MAX_VOID_REASON} disabled={locked} onChange={(event) => setReason(event.target.value)} />
    </FormField>
    {mutation.isError ? <ErrorBanner message={errorMessage(mutation.error)} /> : null}
    <button type="button" className="btn small danger" disabled={mutation.isPending || !reason.trim()}
      onClick={() => mutation.uncertain ? mutation.retry() : mutation.mutate(reason.trim())}>
      {mutation.uncertain ? 'Reintentar anulación' : 'Confirmar anulación'}
    </button>
    <button type="button" className="btn small ghost" disabled={locked} onClick={() => setEditing(false)}>Cancelar anulación</button>
  </div>;
}

/** Visits worth attaching money to: anything not released or cancelled, newest first. */
export function linkableBookings(bookings: Booking[]): Booking[] {
  return bookings
    .filter((booking) => booking.status !== 'released' && booking.status !== 'cancelled')
    .slice()
    .sort((a, b) => b.startsAt.localeCompare(a.startsAt));
}

/** A YYYY-MM-DD from the date input becomes local noon of that day, so the row lands on the day the owner meant. */
export function chargedAtFor(ymd: string): string {
  const [year = 0, month = 1, day = 1] = ymd.split('-').map(Number);
  return new Date(year, month - 1, day, 12, 0, 0, 0).toISOString();
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos guardar el pago.';
}
